import { __assign, __rest, __spreadArray } from 'tslib';
import React, { forwardRef, useRef, useMemo, useEffect, useCallback } from 'react';
import mergeRefs from 'react-merge-refs';
import { ResizeObserver } from '@juggle/resize-observer';
import cn from 'classnames';
import { compute } from 'compute-scroll-into-view';
import { useMultipleSelection, useCombobox } from 'downshift';
import { fnUtils, getDataTestId } from '@alfalab/core-components-shared/esm';
import { useLayoutEffect_SAFE_FOR_SSR } from '@alfalab/hooks';
import { defaultAccessor, defaultFilterFn, defaultGroupAccessor, processOptions, isGroup } from '../../utils.js';
import { NativeSelect } from '../native-select/Component.js';
import { getListPopoverDesktopProps } from './components/list-desktop/helpers/get-list-popover-desktop-props.js';
import { s as styles, L as ListPopoverDesktop } from '../../list-popover-desktop-3ac012fc.js';
import { getListModalMobileProps } from './components/list-mobile/helpers/get-list-modal-mobile-props.js';
import { getListBottomSheetMobileProps } from './components/list-mobile/helpers/get-list-bottom-sheet-mobile-props.js';
import { ListMobile } from './components/list-mobile/list-mobile.js';
import { m as mobileStyles } from '../../mobile.module-a0ce11d9.js';

var itemToString = function (option) { return (option ? option.key : ''); };
var isItemDisabled = function (option) { return Boolean(option === null || option === void 0 ? void 0 : option.disabled); };
var BaseSelect = forwardRef(
// TODO: 😭
// eslint-disable-next-line complexity
function (props, ref) {
    var _a;
    var _b, _c;
    var dataTestId = props.dataTestId, className = props.className, fieldClassName = props.fieldClassName, optionGroupClassName = props.optionGroupClassName, optionsListClassName = props.optionsListClassName, optionClassName = props.optionClassName, options = props.options, _d = props.autocomplete, autocomplete = _d === void 0 ? false : _d, _e = props.multiple, multiple = _e === void 0 ? false : _e, _f = props.allowUnselect, allowUnselect = _f === void 0 ? false : _f, _g = props.disabled, disabled = _g === void 0 ? false : _g, _h = props.closeOnSelect, closeOnSelect = _h === void 0 ? !multiple : _h, _j = props.circularNavigation, circularNavigation = _j === void 0 ? false : _j, _k = props.nativeSelect, nativeSelect = _k === void 0 ? false : _k, _l = props.defaultOpen, defaultOpen = _l === void 0 ? false : _l, openProp = props.open, _m = props.optionsListWidth, optionsListWidth = _m === void 0 ? 'content' : _m, name = props.name, id = props.id, selected = props.selected, _o = props.size, size = _o === void 0 ? 48 : _o, _p = props.optionsSize, optionsSize = _p === void 0 ? size : _p, error = props.error, hint = props.hint, block = props.block, label = props.label, labelView = props.labelView, placeholder = props.placeholder, _q = props.fieldProps, fieldProps = _q === void 0 ? {} : _q, _r = props.optionsListProps, optionsListProps = _r === void 0 ? {} : _r, _s = props.optionProps, optionProps = _s === void 0 ? {} : _s, _t = props.groupOptionProps, groupOptionProps = _t === void 0 ? {} : _t, _u = props.searchProps, searchProps = _u === void 0 ? {} : _u, _v = props.showSearch, showSearch = _v === void 0 ? false : _v, valueRenderer = props.valueRenderer, onChange = props.onChange, onOpen = props.onOpen, onFocus = props.onFocus, onBlur = props.onBlur, onScroll = props.onScroll, onClear = props.onClear, clear = props.clear, Arrow = props.Arrow, _w = props.Field, Field = _w === void 0 ? function () { return null; } : _w, _x = props.OptionsList, OptionsList = _x === void 0 ? function () { return null; } : _x, _y = props.Optgroup, Optgroup = _y === void 0 ? function () { return null; } : _y, _z = props.Option, Option = _z === void 0 ? function () { return null; } : _z, _0 = props.Search, Search = _0 === void 0 ? function () { return null; } : _0, _1 = props.showEmptyOptionsList, showEmptyOptionsList = _1 === void 0 ? false : _1, visibleOptions = props.visibleOptions, view = props.view, _2 = props.isBottomSheet, isBottomSheet = _2 === void 0 ? true : _2, modalProps = props.modalProps, bottomSheetProps = props.bottomSheetProps, limitDynamicOptionGroupSize = props.limitDynamicOptionGroupSize;
    var shouldSearchBlurRef = useRef(true);
    var rootRef = useRef(null);
    var fieldRef = useRef(null);
    var listRef = useRef(null);
    var initiatorRef = useRef(null);
    var searchRef = useRef(null);
    var scrollableContainerRef = useRef(null);
    var onOpenRef = useRef(onOpen);
    var _3 = React.useState(''), searchState = _3[0], setSearchState = _3[1];
    var _4 = typeof (searchProps === null || searchProps === void 0 ? void 0 : searchProps.value) === 'string'
        ? [searchProps.value, searchProps.onChange]
        : [searchState, setSearchState], search = _4[0], setSearch = _4[1];
    var accessor = searchProps.accessor || defaultAccessor;
    var filterFn = searchProps.filterFn || defaultFilterFn;
    var filterGroup = (_b = searchProps.filterGroup) !== null && _b !== void 0 ? _b : false;
    var groupAccessor = (_c = searchProps.groupAccessor) !== null && _c !== void 0 ? _c : defaultGroupAccessor;
    var _5 = useMemo(function () {
        return processOptions(options, selected, function (option) {
            if (isGroup(option)) {
                var groupAccessorValue = groupAccessor(option);
                return (typeof groupAccessorValue === 'string' &&
                    filterFn(groupAccessorValue, search));
            }
            return filterFn(accessor(option), search);
        }, filterGroup);
    }, [options, selected, filterFn, accessor, search, filterGroup, groupAccessor]), filteredOptions = _5.filteredOptions, flatOptions = _5.flatOptions, selectedOptions = _5.selectedOptions;
    var scrollIntoView = function (node) {
        if (!node || view === 'mobile')
            return;
        requestAnimationFrame(function () {
            var actions = compute(node, {
                boundary: listRef.current,
                block: 'nearest',
                scrollMode: 'if-needed',
            });
            actions.forEach(function (action) {
                var el = action.el, top = action.top;
                el.scrollTop = top;
            });
        });
    };
    var useMultipleSelectionProps = {
        itemToString: itemToString,
        onSelectedItemsChange: function (changes) {
            if (onChange) {
                var _a = changes.selectedItems, selectedItems_1 = _a === void 0 ? [] : _a;
                onChange({
                    selectedMultiple: selectedItems_1,
                    selected: selectedItems_1.length ? selectedItems_1[0] : null,
                    initiator: initiatorRef.current,
                    name: name,
                });
                initiatorRef.current = null;
            }
        },
        stateReducer: function (state, actionAndChanges) {
            var type = actionAndChanges.type, changes = actionAndChanges.changes;
            if (!allowUnselect &&
                type === useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace) {
                return state;
            }
            return changes;
        },
    };
    if (selected !== undefined) {
        useMultipleSelectionProps.selectedItems = selectedOptions;
    }
    onOpenRef.current = onOpen;
    var _6 = useMultipleSelection(useMultipleSelectionProps), selectedItems = _6.selectedItems, addSelectedItem = _6.addSelectedItem, setSelectedItems = _6.setSelectedItems, removeSelectedItem = _6.removeSelectedItem, getDropdownProps = _6.getDropdownProps;
    var _7 = useCombobox({
        id: id,
        isOpen: openProp,
        items: flatOptions,
        itemToString: itemToString,
        isItemDisabled: isItemDisabled,
        defaultHighlightedIndex: selectedItems.length === 0 ? -1 : undefined,
        scrollIntoView: scrollIntoView,
        onSelectedItemChange: function (changes) {
            var selectedItem = changes.selectedItem || initiatorRef.current;
            if (selectedItem && !selectedItem.disabled) {
                var alreadySelected = selectedItems.includes(selectedItem);
                var allowRemove = allowUnselect || (multiple && selectedItems.length > 1);
                if (alreadySelected && allowRemove) {
                    removeSelectedItem(selectedItem);
                }
                if (!alreadySelected) {
                    if (multiple) {
                        addSelectedItem(selectedItem);
                    }
                    else {
                        setSelectedItems([selectedItem]);
                    }
                }
            }
        },
        onIsOpenChange: function (changes) {
            if (onOpenRef.current) {
                /**
                 *  Вызываем обработчик асинхронно.
                 *
                 * Иначе при клике вне открытого селекта сначала сработает onOpen, который закроет селект,
                 * А затем сработает onClick кнопки открытия\закрытия с open=false и в итоге селект откроется снова.
                 */
                setTimeout(function () {
                    var _a;
                    (_a = onOpenRef.current) === null || _a === void 0 ? void 0 : _a.call(onOpenRef, {
                        open: changes.isOpen,
                        name: name,
                    });
                }, 0);
            }
            if (showSearch) {
                if (changes.isOpen && view === 'desktop') {
                    setTimeout(function () {
                        var _a;
                        (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    }, 0);
                }
                else {
                    setSearch === null || setSearch === void 0 ? void 0 : setSearch('');
                }
            }
        },
        stateReducer: function (state, actionAndChanges) {
            var type = actionAndChanges.type, changes = actionAndChanges.changes;
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
                    if (!circularNavigation &&
                        state.highlightedIndex === flatOptions.length - 1) {
                        return __assign(__assign({}, changes), { highlightedIndex: state.highlightedIndex });
                    }
                    return changes;
                case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
                    if (!circularNavigation && state.highlightedIndex === 0) {
                        return __assign(__assign({}, changes), { highlightedIndex: state.highlightedIndex });
                    }
                    return changes;
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick: {
                    var selectedItem = changes.selectedItem;
                    initiatorRef.current = selectedItem || null;
                    return __assign(__assign({}, changes), { selectedItem: state.selectedItem === selectedItem ? null : selectedItem, isOpen: !closeOnSelect || (view === 'mobile' && multiple), 
                        // при closeOnSelect === false - сохраняем подсвеченный индекс
                        highlightedIndex: state.isOpen && !closeOnSelect
                            ? state.highlightedIndex
                            : changes.highlightedIndex });
                }
                default:
                    return changes;
            }
        },
    }), open = _7.isOpen, getMenuProps = _7.getMenuProps, getInputProps = _7.getInputProps, getItemProps = _7.getItemProps, getLabelProps = _7.getLabelProps, highlightedIndex = _7.highlightedIndex, toggleMenu = _7.toggleMenu, openMenu = _7.openMenu, closeMenu = _7.closeMenu, setHighlightedIndex = _7.setHighlightedIndex;
    useEffect(function () {
        /*
         * При изменении openProp, состояние внутри downshift в useEnhancedReducer не меняется, поэтому меняем его таким способом
         * Скорее всего это исправят в будущих версиях downshift
         */
        if (openProp !== undefined) {
            if (openProp) {
                openMenu();
            }
            else {
                closeMenu();
            }
        }
    }, [openProp, openMenu, closeMenu]);
    var inputProps = getInputProps(getDropdownProps({ ref: mergeRefs([ref, fieldRef]) }));
    var _8 = getMenuProps({ ref: listRef }, { suppressRefError: true }), menuRef = _8.ref, menuProps = __rest(_8, ["ref"]);
    var handleEntered = function (node, isAppearing) {
        var _a, _b, _c, _d, _e;
        if (showSearch)
            (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        if (isBottomSheet) {
            (_c = (_b = bottomSheetProps === null || bottomSheetProps === void 0 ? void 0 : bottomSheetProps.transitionProps) === null || _b === void 0 ? void 0 : _b.onEntered) === null || _c === void 0 ? void 0 : _c.call(_b, node, isAppearing);
        }
        else {
            (_e = (_d = modalProps === null || modalProps === void 0 ? void 0 : modalProps.transitionProps) === null || _d === void 0 ? void 0 : _d.onEntered) === null || _e === void 0 ? void 0 : _e.call(_d, node, isAppearing);
        }
    };
    var handleFieldFocus = function (event) {
        if (onFocus)
            onFocus(event);
        if (autocomplete && !open) {
            openMenu();
        }
    };
    var handleFieldBlur = function (event) {
        var _a, _b, _c;
        if (view === 'desktop') {
            var isNextFocusInsideField = (_a = fieldRef.current) === null || _a === void 0 ? void 0 : _a.contains((event.relatedTarget || document.activeElement));
            var isNextFocusInsideList = (_b = listRef.current) === null || _b === void 0 ? void 0 : _b.contains((event.relatedTarget || document.activeElement));
            if (!isNextFocusInsideField && !isNextFocusInsideList) {
                if (onBlur)
                    onBlur(event);
                (_c = inputProps.onBlur) === null || _c === void 0 ? void 0 : _c.call(inputProps, event);
            }
        }
        if (view === 'mobile' && !open && onBlur)
            onBlur(event);
    };
    var handleFieldKeyDown = function (event) {
        var _a;
        (_a = inputProps.onKeyDown) === null || _a === void 0 ? void 0 : _a.call(inputProps, event);
        // https://caniuse.com/?search=KeyboardEvent.key
        var isKeyUnsupported = event.key === 'Unidentified';
        if (autocomplete &&
            !open &&
            (isKeyUnsupported || event.key.length === 1 || event.key === 'Backspace')) {
            // Для автокомплита - открываем меню при начале ввода
            openMenu();
        }
        if ([' ', 'Enter'].includes(event.key) &&
            !autocomplete &&
            !nativeSelect &&
            event.target.tagName !== 'INPUT' &&
            event.target.tagName !== 'BUTTON') {
            // Открываем\закрываем меню по нажатию enter или пробела
            event.preventDefault();
            if (!open || highlightedIndex === -1)
                toggleMenu();
        }
    };
    var handleFieldClick = function (event) {
        if (!autocomplete || event.target.tagName !== 'INPUT') {
            toggleMenu();
        }
        else {
            openMenu();
        }
    };
    var handleNativeSelectChange = useCallback(function (event) {
        setSelectedItems(
        // eslint-disable-next-line
        __spreadArray([], event.target.options, true).reduce(function (acc, option, index) {
            return option.selected ? acc.concat(flatOptions[index]) : acc;
        }, []));
    }, [flatOptions, setSelectedItems]);
    var getOptionProps = function (option, index) {
        var _a;
        return (__assign(__assign({}, optionProps), { mobile: view === 'mobile', className: cn(optionClassName, (_a = {},
                _a[mobileStyles.option] = view === 'mobile',
                _a)), innerProps: getItemProps({
                index: index,
                item: option,
                onMouseDown: function (event) { return event.preventDefault(); },
                onClick: function () {
                    var _a, _b;
                    if (view === 'mobile' || !showSearch || multiple)
                        return;
                    shouldSearchBlurRef.current = false;
                    (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.blur();
                    shouldSearchBlurRef.current = true;
                    (_b = fieldRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                },
            }), multiple: multiple, index: index, option: option, size: optionsSize, disabled: option.disabled, highlighted: index === highlightedIndex, selected: selectedItems.some(function (_a) {
                var key = _a.key;
                return key === option.key;
            }), dataTestId: getDataTestId(dataTestId, 'option') }));
    };
    useEffect(function () {
        if (defaultOpen)
            openMenu();
    }, [defaultOpen, openMenu]);
    useEffect(function () {
        if (openProp) {
            openMenu();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var calcOptionsListWidth = useCallback(function () {
        if (view === 'desktop' && listRef.current) {
            var widthAttr = optionsListWidth === 'field' ? 'width' : 'minWidth';
            var optionsListMinWidth = rootRef.current
                ? rootRef.current.getBoundingClientRect().width
                : 0;
            listRef.current.setAttribute('style', '');
            listRef.current.style[widthAttr] = "".concat(optionsListMinWidth, "px");
        }
    }, [view, optionsListWidth]);
    useEffect(function () {
        if (view === 'desktop') {
            var ResizeObserver_1 = window.ResizeObserver || ResizeObserver;
            var observer_1 = new ResizeObserver_1(calcOptionsListWidth);
            if (rootRef.current) {
                observer_1.observe(rootRef.current);
            }
            return function () {
                observer_1.disconnect();
            };
        }
        return fnUtils.noop;
    }, [view, calcOptionsListWidth, open, optionsListWidth]);
    useLayoutEffect_SAFE_FOR_SSR(calcOptionsListWidth, [
        open,
        optionsListWidth,
        filteredOptions,
        selectedItems,
    ]);
    var renderValue = function () {
        return selectedItems.map(function (option) { return (React.createElement("input", { type: 'hidden', name: name, value: option.key, key: option.key })); });
    };
    var renderNativeSelect = function () {
        var value = multiple
            ? selectedItems.map(function (option) { return option.key; })
            : (selectedItems[0] || {}).key;
        return (React.createElement(NativeSelect, __assign({}, menuProps, { className: styles.nativeSelect, disabled: disabled, multiple: multiple, name: name, value: value, onChange: handleNativeSelectChange, options: filteredOptions })));
    };
    var handleFieldClear = function (event) {
        setSelectedItems([]);
        onClear === null || onClear === void 0 ? void 0 : onClear(event);
    };
    var renderSearch = function () {
        var _a;
        var _b, _c;
        if (!showSearch)
            return null;
        var handleClear = function (event) {
            var _a, _b;
            setSearch === null || setSearch === void 0 ? void 0 : setSearch('');
            (_b = (_a = searchProps === null || searchProps === void 0 ? void 0 : searchProps.componentProps) === null || _a === void 0 ? void 0 : _a.onClear) === null || _b === void 0 ? void 0 : _b.call(_a, event);
        };
        var handleChange = function (event, payload) {
            var _a, _b;
            setSearch === null || setSearch === void 0 ? void 0 : setSearch(payload.value);
            (_b = (_a = searchProps === null || searchProps === void 0 ? void 0 : searchProps.componentProps) === null || _a === void 0 ? void 0 : _a.onChange) === null || _b === void 0 ? void 0 : _b.call(_a, event, payload);
        };
        var handleBlur = function (event) {
            var _a, _b;
            if (!shouldSearchBlurRef.current)
                return;
            (_b = (_a = searchProps.componentProps) === null || _a === void 0 ? void 0 : _a.onBlur) === null || _b === void 0 ? void 0 : _b.call(_a, event);
            handleFieldBlur(event);
        };
        return (React.createElement(Search, __assign({}, searchProps === null || searchProps === void 0 ? void 0 : searchProps.componentProps, { onBlur: handleBlur, value: search, onChange: handleChange, dataTestId: getDataTestId(dataTestId, 'search'), onClear: handleClear, className: cn((_b = searchProps === null || searchProps === void 0 ? void 0 : searchProps.componentProps) === null || _b === void 0 ? void 0 : _b.className, (_a = {},
                _a[styles.search] = view === 'desktop',
                _a[mobileStyles.search] = view === 'mobile',
                _a)), ref: mergeRefs([searchRef, ((_c = searchProps === null || searchProps === void 0 ? void 0 : searchProps.componentProps) === null || _c === void 0 ? void 0 : _c.ref) || null]) })));
    };
    var _9 = optionsListProps, header = _9.header, emptyPlaceholder = _9.emptyPlaceholder;
    var renderOptionsListHeader = function () {
        if (header || (view === 'desktop' && showSearch)) {
            return (React.createElement(React.Fragment, null,
                header,
                view === 'desktop' && renderSearch()));
        }
        return null;
    };
    var renderEmptyPlaceholder = function () {
        if (emptyPlaceholder) {
            return emptyPlaceholder;
        }
        if (showSearch) {
            return React.createElement("div", { className: styles.emptySearchPlaceholder }, "\u041D\u0438\u0447\u0435\u0433\u043E \u043D\u0435 \u043D\u0430\u0448\u043B\u043E\u0441\u044C");
        }
        return undefined;
    };
    var renderOptionsList = function () {
        var _a, _b, _c;
        if (flatOptions.length === 0 && !showEmptyOptionsList && !showSearch)
            return null;
        var listProps = optionsListProps;
        return (React.createElement("div", __assign({}, menuProps, { ref: view === 'desktop' ? menuRef : undefined, className: cn(optionsListClassName, view === 'mobile' && mobileStyles.optionsListWrapper, view === 'desktop' && styles.optionsListWrapper) }),
            React.createElement(OptionsList, __assign({}, listProps, { ref: view === 'desktop' ? listProps.ref : scrollableContainerRef, setHighlightedIndex: view === 'desktop' ? setHighlightedIndex : undefined, className: cn((_a = {}, _a[mobileStyles.optionsList] = view === 'mobile', _a)), scrollbarClassName: cn((_b = {}, _b[mobileStyles.scrollbar] = view === 'mobile', _b)), optionsListWidth: optionsListWidth, flatOptions: flatOptions, highlightedIndex: highlightedIndex, open: open, size: size, options: filteredOptions, Optgroup: Optgroup, Option: Option, selectedItems: selectedItems, setSelectedItems: setSelectedItems, toggleMenu: toggleMenu, groupOptionProps: groupOptionProps, getOptionProps: getOptionProps, visibleOptions: view === 'desktop' ? visibleOptions : 0, dataTestId: getDataTestId(dataTestId, 'options-list'), header: renderOptionsListHeader(), optionGroupClassName: cn(optionGroupClassName, (_c = {},
                    _c[mobileStyles.optionGroup] = view === 'mobile',
                    _c)), emptyPlaceholder: renderEmptyPlaceholder(), onScroll: onScroll, search: search, multiple: multiple, limitDynamicOptionGroupSize: limitDynamicOptionGroupSize })),
            view === 'desktop' && React.createElement("div", { className: styles.optionsListBorder })));
    };
    return (React.createElement("div", __assign({}, (disabled && { 'aria-disabled': true }), { "aria-expanded": inputProps['aria-expanded'], "aria-haspopup": 'listbox', role: inputProps.role, className: cn(styles.component, (_a = {}, _a[styles.block] = block, _a), className), ref: rootRef, onKeyDown: disabled ? undefined : handleFieldKeyDown, tabIndex: -1, "data-test-id": getDataTestId(dataTestId) }),
        nativeSelect && renderNativeSelect(),
        React.createElement(Field, __assign({ selectedMultiple: selectedItems, selected: selectedItems[0], setSelectedItems: setSelectedItems, toggleMenu: toggleMenu, multiple: multiple, open: open, disabled: disabled, size: size, placeholder: placeholder, label: label && React.createElement("span", __assign({}, getLabelProps()), label), labelView: labelView, Arrow: Arrow && React.createElement(Arrow, { open: open, size: size, disabled: disabled }), error: error, hint: hint, valueRenderer: valueRenderer, className: fieldClassName, clear: clear, onClear: handleFieldClear, innerProps: {
                onBlur: handleFieldBlur,
                onFocus: disabled ? undefined : handleFieldFocus,
                onClick: disabled ? undefined : handleFieldClick,
                tabIndex: disabled ? undefined : nativeSelect ? -1 : 0,
                ref: inputProps.ref,
                id: inputProps.id,
                'aria-labelledby': inputProps['aria-labelledby'],
                'aria-controls': inputProps['aria-controls'],
                'aria-autocomplete': autocomplete
                    ? inputProps['aria-autocomplete']
                    : undefined,
            }, dataTestId: getDataTestId(dataTestId, 'field') }, fieldProps)),
        name && !nativeSelect && renderValue(),
        view === 'desktop' && !nativeSelect && (React.createElement(ListPopoverDesktop, __assign({}, getListPopoverDesktopProps(props), { open: open, fieldRef: fieldRef, renderOptionsList: renderOptionsList }))),
        view === 'mobile' && (React.createElement(ListMobile, { baseProps: isBottomSheet
                ? getListBottomSheetMobileProps(props)
                : getListModalMobileProps(props), open: open, menuRef: menuRef, scrollableContainerRef: scrollableContainerRef, flatOptions: flatOptions, renderOptionsList: renderOptionsList, renderSearch: renderSearch, closeMenu: closeMenu, handleEntered: handleEntered }))));
});

export { BaseSelect };
